/**
 * CheckUp Score Scale — the 3-tier ladder (NOOB → POTENTIAL → PRO) drawn as
 * "Your position on the scale" on both journey-web (`ScoreScale` in
 * HeroReveal) and citizen-mobile (`CheckUpScoreScale`). Pure,
 * platform-agnostic — no React, no DOM, no RN.
 *
 * It DRAWS what the server sends and decides nothing: the current tier is the
 * badge the run was awarded (`badgeMeta.badgeId`), and the tiers' ranges are
 * the badge lower bounds sent beside the score (`badgeLowerBounds`). It used
 * to grade the score itself (`badgeForScore` over a shared constant), so the
 * scale could mark a tier the run was not awarded.
 */

import type { IQultureBadgeLowerBounds } from '@tonnie/types';

/**
 * Fixed horizontal positions (percent) of the three tier nodes on the track.
 * The marker is quantized to the current band, not linearly placed within it,
 * so both platforms render the node at the same spot.
 */
export const SCORE_SCALE_NODE_POSITIONS = [6, 50, 94] as const;

export type ScoreScaleTierId = 'noob' | 'potential' | 'pro';

/**
 * The i18n key of each badge — the one map every surface translates a badge
 * through (`t(IQULTURE_BADGE_LABEL_KEY[id])`), guarded against en.json in
 * dynamic-key-coverage.spec.ts.
 */
export const IQULTURE_BADGE_LABEL_KEY = {
  noob: 'checkup.band.noob',
  potential: 'checkup.band.potential',
  pro: 'checkup.band.pro',
} as const satisfies Record<ScoreScaleTierId, string>;

/** A dashboard's upper-case band (the server's badge, as sent) → its badge id. */
export function badgeIdForBand(band: 'NOOB' | 'POTENTIAL' | 'PRO'): ScoreScaleTierId {
  switch (band) {
    case 'PRO':
      return 'pro';
    case 'POTENTIAL':
      return 'potential';
    case 'NOOB':
      return 'noob';
  }
}

export interface ScoreScaleTier {
  id: ScoreScaleTierId;
  /** Inclusive lower bound shown in the range label (e.g. 50). */
  rangeMin: number;
  /** Inclusive upper bound shown in the range label (e.g. 80). */
  rangeMax: number;
}

export interface ScoreScaleModel {
  /** Always [noob, potential, pro], in ladder order, with the server's bounds. */
  tiers: [ScoreScaleTier, ScoreScaleTier, ScoreScaleTier];
  /** Index into `tiers` of the badge the run was awarded (0 | 1 | 2). */
  currentIndex: 0 | 1 | 2;
  /** Points from the score to the next tier's lower bound (never negative), or `null` when PRO. */
  pointsToNext: number | null;
  /** Id of the next tier up, or `null` when already PRO. */
  nextTierId: ScoreScaleTierId | null;
}

export interface ScoreScaleInput {
  /** The run's final iQulture score. */
  score: number;
  /** The badge the SERVER awarded the run — the tier the scale marks. */
  badge: ScoreScaleTierId;
  /** Where the badges begin, as the server sent them beside the score. */
  badgeLowerBounds: IQultureBadgeLowerBounds;
}

const LADDER: readonly ScoreScaleTierId[] = ['noob', 'potential', 'pro'];

/** The scale for one scored run, from what the server sent with it. */
export function deriveScoreScale({
  score,
  badge,
  badgeLowerBounds,
}: ScoreScaleInput): ScoreScaleModel {
  const tiers: [ScoreScaleTier, ScoreScaleTier, ScoreScaleTier] = [
    { id: 'noob', rangeMin: 0, rangeMax: badgeLowerBounds.potential },
    { id: 'potential', rangeMin: badgeLowerBounds.potential, rangeMax: badgeLowerBounds.pro },
    { id: 'pro', rangeMin: badgeLowerBounds.pro, rangeMax: 100 },
  ];
  const currentIndex = LADDER.indexOf(badge) as 0 | 1 | 2;
  const next = currentIndex < 2 ? tiers[currentIndex + 1] : null;
  return {
    tiers,
    currentIndex,
    pointsToNext: next ? Math.max(0, next.rangeMin - score) : null,
    nextTierId: next?.id ?? null,
  };
}
